import * as yup from 'yup';
import { KpiPeriod } from '../data-models/company-financials.data-model';
import { ICompanyDataModel } from '../data-models/company.data-model';
import { RendererType } from '../data-models/field-definition';
import { createFormField } from '../view-models/form.view-model';
import { companyFields } from './Company.schema';
import { KpiCollectionConfigurationResponse } from './KpiCollectionConfiguration.schema';
import { emailArraySchema } from './template-assignment-schema';

export function kpiCollectionCompanySettingsFields(
  kpiCollectionSettings: KpiCollectionConfigurationResponse[]
) {
  const kpiCollectionSettingsField = getKpiCollectionSettingsField(kpiCollectionSettings);
  return {
    id: yup.string(),
    kpiCollectionSettingId: yup.string().label('Configuration').formMeta({
      renderer: kpiCollectionSettingsField.renderer,
      rendererMeta: kpiCollectionSettingsField.rendererMeta,
    }),
    companyId: yup.number().required().label('Company').formMeta({
      renderer: RendererType.companyId,
    }),
    granularity: yup
      .string()
      .oneOf(Object.values(KpiPeriod))
      .formMeta({
        renderer: RendererType.singleSelect,
        rendererMeta: {
          values: Object.values(KpiPeriod).map((period) => ({
            value: period,
            displayName: period,
          })),
        },
      }),
  };
}

export function kpiCollectionCompanySettingsSchema(
  kpiCollectionSettings: KpiCollectionConfigurationResponse[]
) {
  return yup.object(kpiCollectionCompanySettingsFields(kpiCollectionSettings));
}

export type KpiCollectionCompanySettings = yup.InferType<
  ReturnType<typeof kpiCollectionCompanySettingsSchema>
>;

export function getKpiCollectionSettingsField(kpiCollectionSettings: KpiCollectionConfigurationResponse[]) {
  return createFormField({
    key: 'kpiCollectionSettingId',
    renderer: RendererType.singleSelect,
    rendererMeta: {
      values: kpiCollectionSettings.map((setting) => ({
        value: setting.id,
        displayName: setting.name,
      })),
      formatValueToLabel: true,
    },
  });
}
export function getRespondentsField(company?: Pick<ICompanyDataModel, 'respondents'>) {
  return createFormField({
    key: 'respondents',
    renderer: RendererType.freeLabels,
    rendererMeta: {
      multi: true,
      values: company?.respondents?.map((respondent) => ({
        value: respondent,
        displayName: respondent,
      })),
    },
  });
}

export function kpiCollectionCompanySettingsPayloadFields(
  kpiCollectionSettings: KpiCollectionConfigurationResponse[]
) {
  const { id, kpiCollectionSettingId, companyId, granularity } =
    kpiCollectionCompanySettingsFields(kpiCollectionSettings);
  const { respondents, fye } = companyFields();
  const kpiCollectionSettingsField = getKpiCollectionSettingsField(kpiCollectionSettings);
  return {
    id,
    companyId,
    kpiCollectionSettingId: kpiCollectionSettingId.formMeta({
      renderer: kpiCollectionSettingsField.renderer,
      rendererMeta: kpiCollectionSettingsField.rendererMeta,
    }),
    granularity,
    // company fields: send payload to /kpiCollectionCompanySettings, but will modify company data
    fye,
    reporting: yup.boolean(),
    respondents: emailArraySchema(respondents).label('Primary Company Contacts').formMeta({
      formatter: 'stringArray',
      renderer: RendererType.freeLabels,
    }),
  };
}

export function kpiCollectionCompanySettingsPayloadSchema(
  kpiCollectionSettings: KpiCollectionConfigurationResponse[] = []
) {
  return yup.object(kpiCollectionCompanySettingsPayloadFields(kpiCollectionSettings));
}

export type KpiCollectionCompanySettingsPayload = yup.InferType<
  ReturnType<typeof kpiCollectionCompanySettingsPayloadSchema>
>;

export function additionalCompanyFields() {
  return {
    sectorId: yup.number().optional().nullable().label('Sector').formMeta({
      renderer: RendererType.id,
      formatter: 'sector',
    }),
    // simplifies respondent validation, hidden from user and excluded from payload
    domain: yup.string().optional().nullable(),
  };
}
export function kpiCompanySettingsGridRowSchema(kpiCollectionSettings: KpiCollectionConfigurationResponse[]) {
  return yup.object({
    ...additionalCompanyFields(),
    ...kpiCollectionCompanySettingsPayloadFields(kpiCollectionSettings),
  });
}

export type KpiCompanySettingsGridRow = yup.InferType<ReturnType<typeof kpiCompanySettingsGridRowSchema>>;

// Strip display-only fields before sending to API
export function toKpiCompanySettingsPayload({
  domain: _domain,
  sectorId: _sectorId,
  granularity: _granularity,
  ...payload
}: KpiCompanySettingsGridRow): KpiCollectionCompanySettingsPayload {
  return payload;
}

export type BulkUpsertKpiCompanySettingsResponse = {
  processed: KpiCollectionCompanySettings[];
  errors: {
    companyId: number;
    message: string;
  }[];
};
